import type { ReactNode } from "react";
import { extractMentions, extractUrls, linkPreviewLabel, parseFormatted } from "@veto/domain";

/** XSS-safe formatted message body — markers only, never HTML injection. */
export function FormattedText({
  text,
  mentions = false,
  highlight = "",
}: {
  text: string;
  /** Groups only (phone parity). */
  mentions?: boolean;
  /** In-chat search term to mark (case-insensitive). */
  highlight?: string;
}) {
  const spans = parseFormatted(text);
  const links = extractUrls(text);
  return (
    <>
      {spans.map((s, i) => {
        if (s.kind === "text") {
          if (highlight.trim()) return <span key={i}>{marked(s.text, highlight)}</span>;
          return mentions ? <MentionAwareText key={i} text={s.text} /> : <span key={i}>{s.text}</span>;
        }
        if (s.kind === "bold") return <strong key={i}>{s.text}</strong>;
        if (s.kind === "italic") return <em key={i}>{s.text}</em>;
        if (s.kind === "strike") return <s key={i}>{s.text}</s>;
        return (
          <code key={i} className="msg-mono">
            {s.text}
          </code>
        );
      })}
      {links.length > 0 && (
        <div className="link-chips">
          {links.map((l) => (
            <a
              key={`${l.start}-${l.url}`}
              className="link-chip"
              href={l.url}
              target="_blank"
              rel="noopener noreferrer"
              title="Local preview only — no remote page fetch"
            >
              {linkPreviewLabel(l)}
            </a>
          ))}
        </div>
      )}
    </>
  );
}

function MentionAwareText({ text }: { text: string }) {
  const hits = extractMentions(text);
  if (!hits.length) return <span>{text}</span>;
  const parts: ReactNode[] = [];
  let cursor = 0;
  hits.forEach((h, i) => {
    if (h.start > cursor) parts.push(<span key={`t${i}`}>{text.slice(cursor, h.start)}</span>);
    parts.push(
      <span key={`m${i}`} className="mention">
        @{h.displayName}
      </span>,
    );
    cursor = h.end;
  });
  if (cursor < text.length) parts.push(<span key="tail">{text.slice(cursor)}</span>);
  return <>{parts}</>;
}

/** Wrap every case-insensitive occurrence of [q] in <mark> (plain text only — never HTML). */
function marked(text: string, q: string): ReactNode[] {
  const needle = q.trim().toLowerCase();
  const lower = text.toLowerCase();
  const out: ReactNode[] = [];
  let cursor = 0;
  for (let i = lower.indexOf(needle); needle && i >= 0; i = lower.indexOf(needle, i + needle.length)) {
    if (i > cursor) out.push(text.slice(cursor, i));
    out.push(
      <mark key={`h${i}`} className="search-mark">
        {text.slice(i, i + needle.length)}
      </mark>,
    );
    cursor = i + needle.length;
  }
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}
